import { defineStore } from 'pinia'
import { ref } from 'vue'
import { config } from '@/config/env'

// ISO3 → ISO2
const ISO3_TO_ISO2 = {
  FRA: 'FR', BEL: 'BE', CHE: 'CH', CMR: 'CM',
  SEN: 'SN', CIV: 'CI', MAR: 'MA', TUN: 'TN', DZA: 'DZ',
  USA: 'US', GBR: 'GB', CAN: 'CA', AUS: 'AU',
  ESP: 'ES', MEX: 'MX', ARG: 'AR',
  DEU: 'DE', AUT: 'AT', BOL: 'BO',
}

// ISO2 → ISO3 (inverse, pour normaliser les codes à 2 lettres venus de la BDD)
const ISO2_TO_ISO3 = Object.fromEntries(
  Object.entries(ISO3_TO_ISO2).map(([k, v]) => [v, k])
)

const ISO3_TO_LOCALE = {
  FRA: 'fr-FR', BEL: 'fr-BE', CHE: 'fr-CH', CMR: 'fr-CM',
  SEN: 'fr-SN', CIV: 'fr-CI', MAR: 'fr-MA', TUN: 'fr-TN', DZA: 'fr-DZ',
  USA: 'en-US', GBR: 'en-GB', CAN: 'en-CA', AUS: 'en-AU',
  ESP: 'es-ES', MEX: 'es-MX', ARG: 'es-AR',
  DEU: 'de-DE', AUT: 'de-AT', BOL: 'es-BO',
}

// Alias pour les codes devise non-ISO ou mal saisis en BDD
const CURRENCY_ALIASES = {
  FCFA:   'XAF',
  'F CFA':'XAF',
  CFA:    'XAF',
  XFA:    'XAF', // typo fréquente (lettres transposées)
  FCFP:   'XPF',
}

const getFlagEmoji = (iso2) => {
  if (!iso2) return '🌍'
  return [...iso2.toUpperCase()]
    .map(c => String.fromCodePoint(0x1F1E6 + c.charCodeAt(0) - 65))
    .join('')
}

export function normalizeCountry(rawCountry) {
  // Normalise le code : si 2 lettres (ISO2 ex: "CM"), convertit en ISO3 ("CMR")
  const rawCode = rawCountry?.code?.toUpperCase().trim() ?? null
  const iso3 = rawCode
    ? (rawCode.length === 2 ? (ISO2_TO_ISO3[rawCode] ?? rawCode) : rawCode)
    : null
  const iso2 = ISO3_TO_ISO2[iso3] ?? (rawCode?.length === 2 ? rawCode : null)
  const phoneCode = rawCountry?.phoneCode ?? null

  const rawCurrency = rawCountry?.currency ?? null
  const currencyUpper = rawCurrency ? rawCurrency.toUpperCase().trim() : null
  const currency = currencyUpper ? (CURRENCY_ALIASES[currencyUpper] ?? currencyUpper) : null

  const rawRate = rawCountry?.exchangeRate ?? null
  const exchangeRate = rawRate !== null && Number.isFinite(Number(rawRate)) && Number(rawRate) > 0
    ? Number(rawRate)
    : null

  return {
    ...rawCountry,
    id: rawCountry?.id ?? null,
    code: iso3,
    iso3,
    iso2,
    phoneCode,
    prefix: phoneCode ? `+${String(phoneCode).replace(/^\+/, '')}` : null,
    locale: rawCountry?.locale ?? (iso3 ? ISO3_TO_LOCALE[iso3] : 'fr-FR'),
    flag: getFlagEmoji(iso2),
    currency,
    exchangeRate,
  }
}

export const useCountriesStore = defineStore('countries', () => {
  const countries = ref([])

  async function fetchCountries() {
    if (countries.value.length > 0) return
    await _load()
  }

  async function forceRefetch() {
    countries.value = []
    await _load()
  }

  async function _load() {
    try {
      const response = await fetch(`${config.apiUrl}/countries`)
      if (response.ok) {
        const data = await response.json()
        countries.value = Array.isArray(data) ? data.map(normalizeCountry) : []
      }
    } catch (e) {
      console.error('Erreur chargement pays:', e.message)
    }
  }

  return { countries, fetchCountries, forceRefetch }
})

